import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Fade, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { QuickSaleAnnouncementDto, AnnouncementSeverity } from '../../../../services/announcementApiService';

interface Props {
  announcements: QuickSaleAnnouncementDto[];
  /** Animasyon hızı: ms per pixel. Default 30ms/px. Düşürmek hızlandırır. */
  speedMsPerPx?: number;
  /** Minimum scroll süresi (kısa mesajlar için) ms. */
  minDurationMs?: number;
  /** Minimum görünür kalma süresi mesaj ekranda kayarken; default 4000ms. */
  baseDurationMs?: number;
  /**
   * INLINE mod: Toolbar/AppBar içine gömülmüş, başlık ile sağ chip'ler arasındaki
   * boşluğu dolduran yatay marquee. Şeffaf zemin, beyaz metin, başlık+mesaj birlikte kayar.
   * Default: false (eski "şerit" davranışı — Toolbar altında severity-renkli bant).
   */
  inline?: boolean;
}

const SEVERITY_META: Record<AnnouncementSeverity, { color: string; bgcolor: string; Icon: typeof InfoOutlinedIcon }> = {
  Info: { color: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.08)', Icon: InfoOutlinedIcon },
  Warning: { color: '#ed6c02', bgcolor: 'rgba(237, 108, 2, 0.10)', Icon: WarningAmberIcon },
  Critical: { color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.12)', Icon: ErrorOutlineIcon },
};

/** Severity'ye göre öncelik sırası: Critical → Warning → Info, ardından oluşturma tarihi (yeniden eskiye). */
function sortBySeverity(items: QuickSaleAnnouncementDto[]): QuickSaleAnnouncementDto[] {
  const order: Record<AnnouncementSeverity, number> = { Critical: 0, Warning: 1, Info: 2 };
  return [...items].sort((a, b) => {
    const sa = order[a.severity] ?? 3;
    const sb = order[b.severity] ?? 3;
    if (sa !== sb) return sa - sb;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

/**
 * Cycle-one fade-geçişli marquee.
 *
 * Davranış:
 *  - Aktif duyurular Critical → Warning → Info sırasına göre sıralanır.
 *  - Her duyuru: fade-in (300ms) → sağdan sola translateX animasyonu → fade-out (300ms) → next.
 *  - Animasyon süresi mesaj uzunluğuna göre dinamik (~30ms/px standart, 15ms/px AppBar inline).
 *  - Tek duyuru varsa sürekli o döngüye girer (CSS infinite).
 *  - announcements değişirse cycle baştan başlar.
 *
 * `inline=true` modunda:
 *  - Şeffaf zemin, beyaz metin (AppBar'ın primary.main rengi üstüne uygun).
 *  - Sol-fixed başlık bloğu yok; başlık + mesaj birlikte kayar.
 *  - Severity ikonu kayan metnin başında küçük olarak görünür.
 *  - Sağdaki "n / total" sayacı kayan metnin sonunda gösterilir.
 */
const AnnouncementMarquee: React.FC<Props> = ({
  announcements,
  speedMsPerPx = 30,
  minDurationMs = 8000,
  baseDurationMs = 4000,
  inline = false,
}) => {
  const sorted = useMemo(() => sortBySeverity(announcements), [announcements]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const cycleTimerRef = useRef<number | null>(null);

  // announcements listesi değişirse index'i sıfırla
  useEffect(() => {
    setCurrentIdx(0);
    setVisible(true);
  }, [sorted.length, sorted.map((a) => a.id).join(',')]);

  // Cycle yönetimi: scroll süresi kadar bekle, fade-out, sonraki, fade-in.
  // ⚠ INLINE modda cycle KULLANILMIYOR (tüm mesajlar tek metne birleştirilip
  //    sonsuz scroll ediliyor). Burada erken dönüyoruz ki gereksiz timer/state
  //    güncellemesi olmasın.
  useEffect(() => {
    if (inline) return;
    if (sorted.length === 0) return;
    const current = sorted[currentIdx];
    if (!current) return;

    // Mesaj genişliğine göre dinamik süre
    const measuredWidth = measureRef.current?.scrollWidth ?? 200;
    const containerWidth = containerRef.current?.clientWidth ?? window.innerWidth;
    const distance = measuredWidth + containerWidth;
    const scrollDuration = Math.max(minDurationMs, distance * speedMsPerPx + baseDurationMs);

    setVisible(true);

    // Tek duyuru için döngü → fade'siz devam etsin (smooth loop)
    if (sorted.length === 1) {
      // CSS animation infinite olduğu için hiçbir şey yapmıyoruz, render iteration eder
      return;
    }

    if (cycleTimerRef.current) window.clearTimeout(cycleTimerRef.current);
    cycleTimerRef.current = window.setTimeout(() => {
      setVisible(false);
      window.setTimeout(() => {
        setCurrentIdx((prev) => (prev + 1) % sorted.length);
      }, 320);
    }, scrollDuration);

    return () => {
      if (cycleTimerRef.current) window.clearTimeout(cycleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, sorted, speedMsPerPx, minDurationMs, baseDurationMs, inline]);

  if (sorted.length === 0) return null;

  const current = sorted[currentIdx] ?? sorted[0];
  const meta = SEVERITY_META[current.severity] ?? SEVERITY_META.Info;
  const Icon = meta.Icon;
  const isLooping = sorted.length === 1;

  // ---------- INLINE MOD (AppBar içi, beyaz, şeffaf) ----------
  if (inline) {
    // ✨ Tek sürekli akış: tüm aktif duyuru mesajlarını ayraçla birleştir.
    // Severity sırası (Critical → Warning → Info) sortBySeverity ile zaten uygulandı.
    // Cycle/fade YOK; tek `infinite` animation ile sonsuz akar.
    const SEPARATOR = '   •   ';
    const combinedMessage = sorted.map((a) => a.message).join(SEPARATOR);

    // Animasyon süresi: text uzunluğuna göre lineer.
    // padding-left: 100% trick → element width = cw + textWidth → translateX(-100%) ile kayar.
    const cw = containerRef.current?.clientWidth ?? 600;
    const mw = measureRef.current?.scrollWidth ?? 400;
    const duration = Math.max(minDurationMs, (cw + mw) * speedMsPerPx);

    return (
      <Box
        ref={containerRef}
        sx={{
          // ⚠ MUI Toolbar default `align-items: center` olduğu için child'a stretch
          // gelmez. EXPLICIT height veriyoruz; Toolbar minHeight 56-64 px içinde
          // 40 px'lik bir bant dikey olarak ortalanır.
          position: 'relative',
          flex: 1,
          minWidth: 0,
          height: 40,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          mx: 2,
          borderRadius: 1,
          bgcolor: 'rgba(255,255,255,0.06)',
        }}
        data-testid="announcement-marquee-inline"
      >
        {/*
          Klasik CSS marquee:
          - inner Box `display: inline-block` + `padding-left: 100%`
            → element genişliği = container genişliği + text genişliği
            → text container'ın TAM SAĞINDA başlar (görünmez)
          - animation translateX(0) → translateX(-100%)
            → text harf harf sağdan sola akar; en son harf solda kaybolur
          - INFINITE loop → bittiğinde tekrar sağdan girer
        */}
        <Box
          // key: mesajlar değiştiğinde (yeni duyuru eklendi/silindi) animation reset
          key={combinedMessage}
          sx={{
            display: 'inline-block',
            whiteSpace: 'nowrap',
            paddingLeft: '100%',
            willChange: 'transform',
            animation: `gtsMarqueeScrollInline ${duration}ms linear infinite`,
            '@keyframes gtsMarqueeScrollInline': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-100%)' },
            },
          }}
        >
          <Typography
            ref={measureRef as any}
            component="span"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
              letterSpacing: 0.2,
              textShadow: '0 1px 2px rgba(0,0,0,0.25)',
            }}
          >
            {combinedMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------- DEFAULT MOD (severity-renkli alt şerit) ----------
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: 28,
        bgcolor: meta.bgcolor,
        overflow: 'hidden',
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
      }}
      data-testid="announcement-marquee"
    >
      {/* Sol-fixed icon + title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          height: '100%',
          color: meta.color,
          fontWeight: 600,
          flexShrink: 0,
          zIndex: 2,
          bgcolor: meta.bgcolor,
          boxShadow: `4px 0 6px -2px ${meta.bgcolor}`,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
        <Typography
          variant="caption"
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {current.title}
        </Typography>
      </Box>

      {/* Kayan mesaj alanı */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Fade in={visible} timeout={300} unmountOnExit={false} appear>
          <Box
            key={`${current.id}-${currentIdx}`}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              willChange: 'transform',
              animation: `gtsMarqueeScroll ${Math.max(
                minDurationMs,
                ((measureRef.current?.scrollWidth ?? 200) +
                  (containerRef.current?.clientWidth ?? window.innerWidth)) *
                  speedMsPerPx
              )}ms linear ${isLooping ? 'infinite' : '1'}`,
              '@keyframes gtsMarqueeScroll': {
                '0%': { transform: 'translateX(100%)' },
                '100%': { transform: 'translateX(-100%)' },
              },
            }}
          >
            <Typography
              ref={measureRef as any}
              variant="body2"
              sx={{
                color: meta.color,
                fontWeight: 500,
                fontSize: '0.82rem',
                px: 2,
                lineHeight: '28px',
              }}
            >
              {current.message}
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Sağ-fixed sayaç (n / total), sadece çoklu duyuruda */}
      {sorted.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            height: '100%',
            color: meta.color,
            fontSize: '0.7rem',
            fontWeight: 600,
            flexShrink: 0,
            zIndex: 2,
            bgcolor: meta.bgcolor,
            boxShadow: `-4px 0 6px -2px ${meta.bgcolor}`,
            opacity: 0.85,
          }}
        >
          {currentIdx + 1} / {sorted.length}
        </Box>
      )}
    </Box>
  );
};

export default AnnouncementMarquee;
